import { Card, Tabs, Descriptions } from "antd";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DeleteModal from "../../DeleteModal/DeleteModal";
import { Error } from "../../Messages/Messages";
import Invoices from "./Invoices/Invoices";
import "./ProjectDetailsView.css";
import ProjectChats from "../ProjectChats/ProjectChats";
import { SocketContext } from "../../../context/SocketContext";
import { isAuthenticated } from "../../Auth/auth";
import EditProjects from "../EditProjects";
import WholeProjectInvoice from "./Invoices/WholeProjectInvoice";
import { checkSpecificPermission } from "../../../utils/checkPermissions";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import Tagcomp from "../../TagComp/Tagcomp";
import ProjectMilestones from "../Milestones/Milestones";
import { ShowInventory } from "../../Inventory/ShowInventory";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import DropdownComp from "../../Dropdowns/DropdownComp";
import SiteDocsPanel from "../../../serviceProvider/components/siteDocs/SiteDocsPanel";
import { canUseSiteDocs } from "../../../serviceProvider/components/siteDocs/access";
import FundingPanel from "../../../serviceProvider/components/funding/FundingPanel";
import { canUseFunding } from "../../../serviceProvider/components/funding/access";
import SiteDiaryPanel from "../../../serviceProvider/components/siteDiary/SiteDiaryPanel";
import { canUseSiteDiary } from "../../../serviceProvider/components/siteDiary/access";
import ProjectWarrantyPanel from "../../../serviceProvider/components/warranty/ProjectWarrantyPanel";
import { canUseWarranty } from "../../../serviceProvider/components/warranty/access";

const ProjectDetailsView = ({ projects, updateParent }) => {
  const { t } = useTranslation("dashboard");
  const socket = useContext(SocketContext);
  const params = useParams();
  const [project, setProject] = useState({});
  const [isProjectDetailsView, setIsProjectDetailsView] = useState(false);
  const user = isAuthenticated();

  const getData = async id => {
    await axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/projects/get/${id}`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token")
        }
      })
      .then(res => {
        if (res.status === 200 || res.status === 304) {
          setProject(res.data);
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        generalErrorAlert(err);
      });
  };

  useEffect(() => {
    getData(params.id);

    return () => {};
  }, [params.id, socket]);

  const items = [
    {
      key: "1",
      label: t("Overview"),
      children: (
        <Card
          title={<span className="project-details__name">{project?.projectName}</span>}
          extra={
            <DropdownComp
              menuItems={[
                {
                  key: "edit",
                  component: () => <EditProjects data={project} updateParent={updateParent} />
                },
                {
                  key: "delete",
                  component: () => <DeleteModal text="Projects" onClick={() => undefined} />
                }
              ]}
            />
          }
        >
          <div>
            <Descriptions bordered size="middle" column={{ xs: 1, sm: 1, md: 2 }}>
              <Descriptions.Item label={t("Client")}>
                {project?.client?.firstName} {project?.client?.lastName}
              </Descriptions.Item>
              <Descriptions.Item label={t("Start Date")}>
                {dayjs(project?.startDate).format("DD/MMM/YYYY") || null}
              </Descriptions.Item>
              <Descriptions.Item label={t("Total Amount")}>
                {project?.currency} {project?.amount}
              </Descriptions.Item>
              <Descriptions.Item label={t("Project Leader")}>
                <Tagcomp text={project?.projectLeader?.username} />
              </Descriptions.Item>
              <Descriptions.Item label={t("Deadline")}>
                {dayjs(project?.endDate).format("DD/MMM/YYYY") || null}
              </Descriptions.Item>
              <Descriptions.Item label={t("Priority")}>{project?.priority}</Descriptions.Item>
              <Descriptions.Item label={t("Team")}>
                <div className="project-details__team">
                  {project?.team?.map((member, index) => (
                    <Tagcomp key={member?._id || index} text={member?.username} />
                  ))}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label={t("Description")}>{project?.description}</Descriptions.Item>
            </Descriptions>
            <div className="project-details__section">
              <h4>{t("Inventory")}</h4>
              <ShowInventory inventory={project?.inventory} />
            </div>
          </div>
        </Card>
      )
    },
    checkSpecificPermission(user?.roleId?.permissions, "Milestones", "Read")
      ? {
          key: "2",
          label: t("Milestone"),
          children: (
            <React.Suspense fallback={null}>
              <ProjectMilestones project={project} onClickDetailsView={setIsProjectDetailsView} />
            </React.Suspense>
          )
        }
      : null,
    checkSpecificPermission(user?.roleId?.permissions, "Chats", "Read")
      ? {
          key: "3",
          label: t("Chats"),
          children: (
            <React.Suspense fallback={null}>
              <ProjectChats project={project} />
            </React.Suspense>
          )
        }
      : null,
    checkSpecificPermission(user?.roleId?.permissions, "Invoices", "Read")
      ? {
          key: "4",
          label: t("Milestone Invoices"),
          children: (
            <React.Suspense fallback={null}>
              <Invoices />
            </React.Suspense>
          )
        }
      : null,
    checkSpecificPermission(user?.roleId?.permissions, "Invoices", "Read")
      ? {
          key: "5",
          label: t("Whole Project Invoice"),
          children: (
            <React.Suspense fallback={null}>
              <WholeProjectInvoice projectDetails={project} />
            </React.Suspense>
          )
        }
      : null,
    // Site documentation: staff only (the provider, or employees who may document or read it)
    project?._id && canUseSiteDocs()
      ? {
          key: "6",
          label: t("Documentation"),
          children: (
            <SiteDocsPanel
              filter={{ project: project._id }}
              client={project.client?._id || ""}
              customerEmail={project.client?.email || ""}
              reportTitle={project.projectName || ""}
            />
          )
        }
      : null,
    // Fördermittel cases of the project: staff with the "Funding" permission
    project?._id && canUseFunding()
      ? {
          key: "7",
          label: t("Funding"),
          children: <FundingPanel project={project._id} client={project.client?._id || ""} />
        }
      : null,
    // Bautagebuch: staff only (the provider, or employees who may write or read it)
    project?._id && canUseSiteDiary()
      ? {
          key: "8",
          label: t("Site diary"),
          children: (
            <SiteDiaryPanel project={project._id} clientEmail={project.client?.email || ""} />
          )
        }
      : null,
    // Abnahme, Gewährleistung, Bürgschaften of this project
    project?._id && canUseWarranty()
      ? {
          key: "9",
          label: t("Acceptance & warranty"),
          children: <ProjectWarrantyPanel project={project._id} />
        }
      : null
  ].filter(Boolean);

  return (
    <div className="project-details-page">
      <Tabs
        rootClassName={isProjectDetailsView ? "hideTabsMenu" : undefined}
        type="card"
        className="projectTabs"
        defaultActiveKey="1"
        items={items}
      />
    </div>
  );
};

export default ProjectDetailsView;
